import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import {
  ZipcodeMarketValue,
  TradeType,
} from "@/modules/stochastic/features/TotalAddressableMarket/api/fetchZipcodeMarketValues/types";
import { fetchZipcodeMarketValues } from "@/modules/stochastic/features/TotalAddressableMarket/api/fetchZipcodeMarketValues/fetchZipcodeMarketValuesApi";

export type ColorMapType = "viridis" | "magma" | "inferno" | "turbo";

interface TotalAddressableMarketState {
  marketData: ZipcodeMarketValue[];
  selectedTrade: TradeType;
  selectedColorMap: ColorMapType;
  loading: boolean;
  error: string | null;
  dataVersion: number;
}

const initialState: TotalAddressableMarketState = {
  marketData: [],
  selectedTrade: "hvac",
  selectedColorMap: "viridis",
  loading: false,
  error: null,
  dataVersion: 0,
};

const totalAddressableMarketSlice = createSlice({
  name: "totalAddressableMarket",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setMarketData: (state, action: PayloadAction<ZipcodeMarketValue[]>) => {
      state.marketData = action.payload;
      state.dataVersion += 1;
    },
    setSelectedTrade: (state, action: PayloadAction<TradeType>) => {
      state.selectedTrade = action.payload;
    },
    setSelectedColorMap: (state, action: PayloadAction<ColorMapType>) => {
      state.selectedColorMap = action.payload;
    },
    clearMarketData: (state) => {
      state.marketData = [];
      state.error = null;
      state.dataVersion = 0;
    },
  },
});

export const {
  setLoading,
  setError,
  setMarketData,
  setSelectedTrade,
  setSelectedColorMap,
  clearMarketData,
} = totalAddressableMarketSlice.actions;

export const fetchZipcodeMarketValuesAction =
  (zipcodes: string[]): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response = await fetchZipcodeMarketValues(zipcodes);
      dispatch(setMarketData(response.data));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch market values. Please try again."));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

export default totalAddressableMarketSlice.reducer;
